import React from "react";

import { formatKstShort } from "../../utils/time";

/**
 * TradesTable — paper-trader /api/strategy/{id}/trades 응답 표시.
 *
 * store.Trade 필드:
 *   EntryTs, ExitTs, Symbol, Side, EntryPrice, ExitPrice,
 *   RetPct      — 이미 퍼센트 단위 (예: -0.86 = -0.86%). 추가 ×100 금지
 *   NotionalKRW — 포지션 진입 시점에 투입한 명목 원화
 *   CostKRW     — 진입+청산 수수료 + 슬리피지 추정치
 *   PnLKRWNet   — NotionalKRW × RetPct/100 − CostKRW (net 손익)
 *   ExitReason  — "tp" | "sl" | "be" | "time"
 */
export default function TradesTable({ trades }) {
    if (!trades || trades.length === 0) {
        return (
            <div className="text-sm text-content-secondary py-4 text-center">
                거래 이력 없음
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-content-muted border-b border-surface-border">
                        <th className="text-left py-2 px-2 font-medium">진입</th>
                        <th className="hidden sm:table-cell text-left py-2 px-2 font-medium">청산</th>
                        <th className="text-left py-2 px-2 font-medium">심볼</th>
                        <th className="text-left py-2 px-2 font-medium">방향</th>
                        <th className="hidden sm:table-cell text-right py-2 px-2 font-medium">투입(KRW)</th>
                        <th className="text-right py-2 px-2 font-medium">수익률</th>
                        <th className="text-right py-2 px-2 font-medium">순손익</th>
                        <th className="text-left py-2 px-2 font-medium">사유</th>
                    </tr>
                </thead>
                <tbody>
                    {trades.map((t, i) => {
                        const side = (t.Side || t.side || "").toLowerCase();
                        const sideClass =
                            side === "long" ? "text-gain" : side === "short" ? "text-loss" : "";
                        // paper-trader 가 RetPct 를 이미 퍼센트로 저장하므로 그대로 표시.
                        const ret = t.RetPct ?? t.ret_pct ?? 0;
                        const pnl = t.PnLKRWNet ?? t.pnl_krw_net ?? 0;
                        const notional = t.NotionalKRW ?? t.notional_krw ?? 0;
                        const valueClass =
                            pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content";
                        // 거래는 EntryTs+ExitTs+Symbol 로 사실상 unique.
                        // 모두 결측 시에만 index 폴백.
                        const k = `${t.EntryTs || t.entry_ts || ""}-${t.ExitTs || t.exit_ts || ""}-${t.Symbol || t.symbol || i}`;
                        return (
                            <tr
                                key={k}
                                className="border-b border-surface-border-light"
                            >
                                <td className="py-2 px-2 text-content-secondary">
                                    {fmtTs(t.EntryTs ?? t.entry_ts)}
                                </td>
                                <td className="hidden sm:table-cell py-2 px-2 text-content-secondary">
                                    {fmtTs(t.ExitTs ?? t.exit_ts)}
                                </td>
                                <td className="py-2 px-2 text-content">
                                    {t.Symbol || t.symbol}
                                </td>
                                <td className={`py-2 px-2 font-semibold ${sideClass}`}>
                                    {(t.Side || t.side || "").toUpperCase()}
                                </td>
                                <td className="hidden sm:table-cell py-2 px-2 text-right text-content">
                                    {Math.round(notional).toLocaleString()}
                                </td>
                                <td className={`py-2 px-2 text-right ${valueClass}`}>
                                    {ret.toFixed(2)}%
                                </td>
                                <td className={`py-2 px-2 text-right font-semibold ${valueClass}`}>
                                    {Math.round(pnl).toLocaleString()}
                                </td>
                                <td className="py-2 px-2 text-content-muted">
                                    {t.ExitReason || t.exit_reason || "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function fmtTs(ts) {
    if (!ts) return "—";
    return formatKstShort(ts);
}
